import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  Event as EventIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext.js';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DashboardService from '../../services/DashboardService.js';

const formatMonthLabel = (date) => {
  return date.toLocaleString(undefined, { month: 'short' });
};

const toYearMonthKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const pickFirst = (obj, keys, fallback) => {
  if (!obj) return fallback;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return fallback;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPatients: 0,
    activePatients: 0,
    todayAppointments: 0,
    pendingBills: 0,
    lowStockItems: 0
  });
  
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch real data from API
        const [summaryResponse, recentAppointmentsResponse, revenueResponse] = await Promise.all([
          DashboardService.getDashboardSummary(),
          DashboardService.getTodayAppointments(),
          DashboardService.getRevenueStatistics('year')
        ]);
        
        // Update stats with real data
        const summaryData = summaryResponse?.data ?? {};
        const totalPatients = Number(pickFirst(summaryData, ['totalPatients', 'TotalPatients'], 0)) || 0;
        const todayAppointments = Number(pickFirst(summaryData, ['todayAppointments', 'TodayAppointments'], 0)) || 0;
        const lowStockItems = Number(pickFirst(summaryData, ['lowStockItems', 'LowStockItems'], 0)) || 0;
        setStats({
          totalPatients,
          activePatients: totalPatients, // Use total patients as active for now
          todayAppointments,
          pendingBills: 0, // Will be implemented later
          lowStockItems
        });
        
        // Transform appointment data for display
        const appointmentsData = recentAppointmentsResponse?.data || [];
        const formattedAppointments = appointmentsData.map((appointment) => {
          const id = pickFirst(appointment, ['id', 'Id', 'appointmentId', 'AppointmentId'], '');
          const patient = pickFirst(appointment, ['patient', 'Patient'], null);
          const patientName =
            pickFirst(appointment, ['patientNickname', 'PatientNickname'], null) ||
            pickFirst(appointment, ['patientName', 'PatientName'], null) ||
            pickFirst(patient, ['nickname', 'Nickname'], null) ||
            pickFirst(patient, ['fullName', 'FullName'], null) ||
            (() => {
              const first = pickFirst(patient, ['firstName', 'FirstName'], '');
              const last = pickFirst(patient, ['lastName', 'LastName'], '');
              const full = `${first} ${last}`.trim();
              return full || null;
            })() ||
            'Unknown Patient';

          const appointmentDateValue = pickFirst(appointment, ['appointmentDate', 'AppointmentDate'], null);
          const startTime = pickFirst(appointment, ['startTime', 'StartTime'], '');
          const dateStr = appointmentDateValue ? new Date(appointmentDateValue).toLocaleDateString() : '';
          const timeStr = startTime ? String(startTime) : '';

          const type =
            pickFirst(appointment, ['type', 'Type'], null) ||
            pickFirst(appointment, ['title', 'Title'], null) ||
            'Appointment';

          return {
            id: id || `${patientName}-${appointmentDateValue ?? ''}-${startTime ?? ''}`,
            patientName,
            date: `${dateStr}${timeStr ? ` ${timeStr}` : ''}`.trim(),
            type
          };
        });
        
        setRecentAppointments(formattedAppointments);

        const monthlyRevenue = revenueResponse?.data?.monthlyRevenue || revenueResponse?.data?.MonthlyRevenue || {};
        const now = new Date();
        const monthsBack = 6;
        const chartData = Array.from({ length: monthsBack }, (_, idx) => {
          const monthOffset = monthsBack - 1 - idx;
          const monthDate = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
          const key = toYearMonthKey(monthDate);
          const amount = Number(monthlyRevenue?.[key] ?? 0);
          return {
            name: formatMonthLabel(monthDate),
            revenue: Number.isFinite(amount) ? amount : 0
          };
        });
        setRevenueData(chartData);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Fallback to some default values on error
        setStats({
          totalPatients: 0,
          activePatients: 0,
          todayAppointments: 0,
          pendingBills: 0,
          lowStockItems: 0
        });
        setRecentAppointments([]);
        setRevenueData([]);
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  const StatCard = ({ title, value, icon, color, onClick }) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="div" color="text.secondary">
            {title}
          </Typography>
          <Avatar sx={{ bgcolor: color }}>
            {icon}
          </Avatar>
        </Box>
        <Typography variant="h4" component="div">
          {value}
        </Typography>
      </CardContent>
      <CardActions>
        <Button size="small" onClick={onClick}>View Details</Button>
      </CardActions>
    </Card>
  );
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>
      
      <Typography variant="h6" gutterBottom>
        Welcome back, {currentUser?.username || 'User'}!
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Total Patients"
            value={stats.totalPatients}
            icon={<PersonIcon />}
            color="#1976d2"
            onClick={() => navigate('/patients')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Active Patients"
            value={stats.activePatients}
            icon={<PersonIcon />}
            color="#2e7d32"
            onClick={() => navigate('/patients?status=active')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Today's Appointments"
            value={stats.todayAppointments}
            icon={<EventIcon />}
            color="#ed6c02"
            onClick={() => navigate('/appointments?date=today')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Pending Bills"
            value={stats.pendingBills}
            icon={<ReceiptIcon />}
            color="#9c27b0"
            onClick={() => navigate('/billing?status=pending')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StatCard
            title="Low Stock Items"
            value={stats.lowStockItems}
            icon={<InventoryIcon />}
            color="#d32f2f"
            onClick={() => navigate('/inventory?stock=low')}
          />
        </Grid>
      </Grid>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Today's Appointments
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {recentAppointments.length > 0 ? (
              <List>
                {recentAppointments.map((appointment, idx) => (
                  <React.Fragment key={appointment.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#1976d2' }}>
                          <CalendarTodayIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={appointment.patientName}
                        secondary={`${appointment.date} - ${appointment.type}`}
                      />
                    </ListItem>
                    {idx !== recentAppointments.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No appointments scheduled for today.
              </Typography>
            )}
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => navigate('/appointments')}>
                View All Appointments
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Revenue Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={revenueData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#1976d2" name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => navigate('/billing')}>
                View Financial Reports
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
